import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Plus, CalendarDays } from "lucide-react";
import api from "@/lib/apiClient";
import { useAuth, hasRole } from "@/context/AuthContext";
import { PageHeader, EmptyState, StatusBadge } from "@/components/dashboard/primitives";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

export default function EventsList() {
  const { role, activeMembership } = useAuth();
  const [events, setEvents] = useState(null);

  useEffect(() => {
    api.get("/events").then(({ data }) => setEvents(data)).catch(() => setEvents([]));
  }, [activeMembership?.org_id]);

  return (
    <div className="p-5 sm:p-8 max-w-6xl">
      <PageHeader title="Events" subtitle="All events in this organisation."
        action={hasRole(role, "org_admin") && (
          <Button asChild className="rounded-md bg-accent text-accent-foreground hover:opacity-90 transition-opacity" data-testid="events-new-btn">
            <Link to="/events/new"><Plus className="h-4 w-4 mr-1.5" /> New Event</Link>
          </Button>
        )} />

      {!events ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-48 rounded-xl" />)}</div>
      ) : events.length === 0 ? (
        <EmptyState icon={CalendarDays} title="No events yet" description="Set up your first event and sessions to get started."
          action={hasRole(role, "org_admin") && <Button asChild className="rounded-md"><Link to="/events/new">Create an event</Link></Button>} />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="events-grid">
          {events.map((e) => (
            <Link key={e.id} to={`/events/${e.id}`} data-testid={`event-card-${e.id}`}
              className="rounded-xl border border-border bg-card overflow-hidden hover:-translate-y-1 transition-transform group">
              {e.cover_image ? (
                <div className="aspect-[16/7] bg-muted"><img src={e.cover_image} alt="" className="h-full w-full object-cover" /></div>
              ) : (
                <div className="aspect-[16/7] bg-primary pitso-grid-bg opacity-90 flex items-center justify-center">
                  <span className="font-display font-bold text-white/90 text-lg px-4 text-center">{e.name}</span>
                </div>
              )}
              <div className="p-5">
                <div className="flex items-start justify-between">
                  <StatusBadge status={e.status} />
                  <span className="text-[10px] font-mono text-muted-foreground bg-muted px-2 py-0.5 rounded">#{e.public_code}</span>
                </div>
                <h3 className="mt-3 font-display text-lg font-bold text-foreground leading-tight">{e.name}</h3>
                <p className="mt-1 text-sm text-muted-foreground line-clamp-2 min-h-[2.5rem]">{e.description || "No description"}</p>
                <div className="mt-3 pt-3 border-t border-border flex items-center gap-4 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {e.venue || e.location || "—"}</span>
                  <span className="capitalize">{e.event_type}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
